import React, { useCallback, useEffect, useRef } from 'react';
import { Info, NotePencil, Warning } from '@phosphor-icons/react';
import {
  Badge,
  Form,
  Overlay,
  OverlayTrigger,
  Popover,
  Tooltip,
} from 'react-bootstrap';
import { components as selectComponents, SingleValue } from 'react-select';
import { CalibrationCell_EmployeeTopicFragment } from '../../../../__generated__/graphql';
import { SelectSingleStyled } from '../../../../../../components/Select/SelectSingleStyled';
import { getRateColor } from '../calibrationColors';
import {
  CalibrationOption,
  useCalibrationWorkspace,
} from '../CalibrationWorkspaceContext';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';

type Props = {
  employeeTopic: CalibrationCell_EmployeeTopicFragment;
};

export const CalibrationCell: React.FC<React.PropsWithChildren<Props>> = ({
  employeeTopic,
}) => {
  const {
    isEditable,
    calibrationOptions,
    pendingChanges,
    conflicts,
    setPendingChange,
    openHistory,
    activeNoteCellId,
    openNotePopover,
    closeNotePopover,
  } = useCalibrationWorkspace();

  const noteIconRef = useRef<HTMLDivElement>(null);
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingChange = pendingChanges.get(employeeTopic.Id);
  const isConflicted = conflicts.has(employeeTopic.Id);
  const t = useNamespacedTranslation('performanceReview.calibration.saveBar');

  const currentRate = pendingChange?.rate ?? employeeTopic.flair__Final_Rate__c;
  const isNotePopoverOpen = activeNoteCellId === employeeTopic.Id;
  const hasNote = !!pendingChange?.notes;

  useEffect(() => {
    if (isNotePopoverOpen) {
      noteTextareaRef.current?.focus({ preventScroll: true });
    }
  }, [isNotePopoverOpen]);

  const handleChange = useCallback(
    (selected: SingleValue<CalibrationOption>) => {
      if (!selected) return;
      const expectedRate =
        employeeTopic.flair__Final_Rate__c ??
        employeeTopic.flair__Initial_Rate__c ??
        null;
      setPendingChange(
        employeeTopic.Id,
        selected.value,
        expectedRate,
        pendingChange?.notes,
      );
      // Delay opening so the dropdown click event finishes before rootClose listens
      setTimeout(() => openNotePopover(employeeTopic.Id), 0);
    },
    [employeeTopic, pendingChange?.notes, setPendingChange, openNotePopover],
  );

  const handleNoteChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!pendingChange) return;
      setPendingChange(
        pendingChange.employeeTopicId,
        pendingChange.rate,
        pendingChange.expectedRate,
        e.target.value || undefined,
      );
    },
    [pendingChange, setPendingChange],
  );

  const handleInfoClick = useCallback(() => {
    openHistory(employeeTopic.Id);
  }, [employeeTopic.Id, openHistory]);

  const handleNoteIconClick = useCallback(() => {
    if (isNotePopoverOpen) {
      closeNotePopover();
    } else {
      openNotePopover(employeeTopic.Id);
    }
  }, [employeeTopic.Id, isNotePopoverOpen, openNotePopover, closeNotePopover]);

  const InfoIcon = (
    <div style={{ paddingTop: '0.125rem' }}>
      <Info
        size={16}
        className="text-muted"
        style={{ cursor: 'pointer' }}
        onClick={handleInfoClick}
      />
    </div>
  );

  const NoteIcon = pendingChange ? (
    <div ref={noteIconRef} style={{ paddingTop: '0.125rem' }}>
      <NotePencil
        size={16}
        className={hasNote ? 'text-primary' : 'text-muted'}
        style={{ cursor: 'pointer' }}
        onClick={handleNoteIconClick}
      />
    </div>
  ) : null;

  const NotePopoverOverlay = (
    <Overlay
      target={noteIconRef.current}
      show={isNotePopoverOpen && !!pendingChange}
      rootClose
      onHide={closeNotePopover}>
      <Popover id={`note-popover-${employeeTopic.Id}`}>
        <Popover.Body>
          <Form.Control
            ref={noteTextareaRef}
            as="textarea"
            size="sm"
            rows={3}
            placeholder={t('notePlaceholder')}
            value={pendingChange?.notes ?? ''}
            onChange={handleNoteChange}
          />
        </Popover.Body>
      </Popover>
    </Overlay>
  );

  const ConflictIcon = isConflicted ? (
    <OverlayTrigger
      overlay={
        <Tooltip id={`conflict-tooltip-${employeeTopic.Id}`}>
          {t('conflictTooltip')}
        </Tooltip>
      }>
      <div style={{ paddingTop: '0.125rem' }}>
        <Warning size={16} className="text-warning" />
      </div>
    </OverlayTrigger>
  ) : null;

  if (!isEditable) {
    const rateLabel =
      currentRate != null
        ? calibrationOptions.find((o) => o.value === currentRate)?.label ??
          currentRate
        : '\u2014';

    const rateColor = getRateColor(currentRate, calibrationOptions);

    return (
      <div className="d-flex align-items-start gap-1">
        <div className="flex-grow-1">
          <Badge
            bg={rateColor ? '' : 'light'}
            text={rateColor ? undefined : 'dark'}
            style={
              rateColor
                ? { backgroundColor: rateColor.bg, color: rateColor.text }
                : undefined
            }
            className="fw-normal">
            {rateLabel}
          </Badge>
        </div>
        {InfoIcon}
      </div>
    );
  }

  if (calibrationOptions.length > 0) {
    const selectedOption =
      calibrationOptions.find((o) => o.value === currentRate) ?? null;

    return (
      <div className="d-flex align-items-start gap-1">
        <div className="flex-grow-1">
          <SelectSingleStyled<CalibrationOption>
            options={calibrationOptions}
            value={selectedOption}
            onChange={handleChange}
            heightMode="small"
            minWidth="8rem"
            isClearable={false}
            isSearchable={false}
            placeholder={'\u2014'}
            getOptionValue={(o) => String(o.value)}
            components={{
              IndicatorSeparator: null,
              Option: ({ data, ...props }) => (
                <selectComponents.Option data={data} {...props}>
                  <RateBadge
                    label={data.label}
                    color={getRateColor(data.value, calibrationOptions)}
                  />
                </selectComponents.Option>
              ),
              SingleValue: ({ data, ...props }) => (
                <selectComponents.SingleValue data={data} {...props}>
                  <RateBadge
                    label={data.label}
                    color={getRateColor(data.value, calibrationOptions)}
                  />
                </selectComponents.SingleValue>
              ),
            }}
          />
        </div>
        {NoteIcon}
        {InfoIcon}
        {ConflictIcon}
        {NotePopoverOverlay}
      </div>
    );
  }

  return (
    <div className="d-flex align-items-start gap-1">
      <div className="flex-grow-1">
        <Form.Control
          type="number"
          size="sm"
          step="0.1"
          value={currentRate ?? ''}
          onChange={(e) => {
            const newRate = parseFloat(e.target.value);
            if (isNaN(newRate)) {
              return;
            }
            const expectedRate =
              employeeTopic.flair__Final_Rate__c ??
              employeeTopic.flair__Initial_Rate__c ??
              null;
            setPendingChange(
              employeeTopic.Id,
              newRate,
              expectedRate,
              pendingChange?.notes,
            );
            setTimeout(() => openNotePopover(employeeTopic.Id), 0);
          }}
        />
      </div>
      {NoteIcon}
      {InfoIcon}
      {ConflictIcon}
      {NotePopoverOverlay}
    </div>
  );
};

const RateBadge: React.FC<
  React.PropsWithChildren<{
    label: string;
    color: { bg: string; text: string } | null;
  }>
> = ({ label, color }) => (
  <Badge
    bg={color ? '' : 'light'}
    text={color ? undefined : 'dark'}
    style={color ? { backgroundColor: color.bg, color: color.text } : undefined}
    className="fw-normal">
    {label}
  </Badge>
);
